import {
    ActionExecutionStatus,
    EngagementLogActionExecutionEventDataDto,
    EngagementLogEventDto,
    FlowStateWithRelationsDto,
} from '@ampsec/platform-client';
import { DEFAULT_WORKFLOW_ROUTE } from 'Core-utils/routes';

export const TRIGGER_DEFERRAL_FLAGS = [
    'welcomeTriggered',
    'waitUntilBusinessTriggered',
    'delayEngagementTriggered',
] as const;

export const WAIT_DEFERRAL_FLAGS = [
    'waitForScheduledActionTriggered',
    'waitForPresenceTriggered',
    'waitUntilBusinessTriggered',
] as const;

type HistoryPayload = Record<string, unknown>;

function getNodeName(event: EngagementLogEventDto): string {
    return String(event.data?.nodeName ?? '').toLowerCase();
}

function getPayload(event: EngagementLogEventDto): HistoryPayload {
    return (event.data?.e?.payload ?? {}) as HistoryPayload;
}

function getFirstTrueFlag(payload: HistoryPayload, flags: readonly string[]): string | undefined {
    return flags.find((flag) => payload[flag] === true);
}

export function shouldRenderStateTransactionEvent(event: EngagementLogEventDto): boolean {
    const nodeName = getNodeName(event);

    if (nodeName === 'wait') {
        return getFirstTrueFlag(getPayload(event), WAIT_DEFERRAL_FLAGS) !== undefined;
    }

    if (nodeName === 'trigger') {
        return getFirstTrueFlag(getPayload(event), TRIGGER_DEFERRAL_FLAGS) !== undefined;
    }

    return true;
}

export function getStateTransactionLabel(event: EngagementLogEventDto): string {
    const nodeName = getNodeName(event);
    const payload = getPayload(event);

    if (nodeName === 'trigger') {
        const deferralFlag = getFirstTrueFlag(payload, TRIGGER_DEFERRAL_FLAGS);
        return deferralFlag
            ? `Engagement trigger deferred: ${deferralFlag}`
            : `Transitioned to ${event.data?.nodeName}`;
    }

    if (nodeName === 'wait') {
        const deferralFlag = getFirstTrueFlag(payload, WAIT_DEFERRAL_FLAGS);
        return deferralFlag
            ? `Transitioned to Wait with reason: ${deferralFlag}`
            : `Transitioned to ${event.data?.nodeName}`;
    }

    if (nodeName === 'ntfyerr') {
        const error = payload.error;
        return error !== undefined && error !== null && error !== ''
            ? `Transitioned to NtfyErr: ${String(error)}`
            : 'Transitioned to NtfyErr';
    }

    if (nodeName === 'ntfy') {
        return 'Triggering reminder notifification.';
    }

    return `Transitioned to ${event.data?.nodeName}`;
}

export function getFlowStateCreatedLabel(flowState: FlowStateWithRelationsDto): string {
    const triggerSource = flowState.meta?.engagementTriggerSource;
    const base = `Flow State ID ${flowState.id} created`;
    return triggerSource ? `${base}: ${triggerSource}` : base;
}

function historyTimestamp(ts: number): string {
    return new Date(ts).toISOString();
}

export function getFlowStateCreatedTimestamp(flowState: FlowStateWithRelationsDto | null | undefined): string {
    const firstHistoryTs = flowState?.history?.[0]?.ts;
    if (typeof firstHistoryTs === 'number') {
        return historyTimestamp(firstHistoryTs);
    }
    return flowState?.createdAt ?? '';
}

export function getFlowStateUpdatedTimestamp(flowState: FlowStateWithRelationsDto | null | undefined): string {
    const history = flowState?.history ?? [];
    const lastHistoryTs = history[history.length - 1]?.ts;
    if (typeof lastHistoryTs === 'number') {
        return historyTimestamp(lastHistoryTs);
    }
    return flowState?.updatedAt ?? '';
}

export function getEngagementLogEventTimestamp(event: EngagementLogEventDto): string {
    const historyTs = event.data?.ts;
    if (typeof historyTs === 'number') {
        return new Date(historyTs).toISOString();
    }

    const dialogueTimestamp = event.data?.timestamp;
    if (typeof dialogueTimestamp === 'string' && dialogueTimestamp) {
        return dialogueTimestamp;
    }

    const executionTime = event.data?.executionTime;
    if (typeof executionTime === 'string' && executionTime) {
        return executionTime;
    }

    return event.timestamp;
}

export function getActionExecutionEventData(event: EngagementLogEventDto): EngagementLogActionExecutionEventDataDto {
    return event.data as EngagementLogActionExecutionEventDataDto;
}

export function getActionExecutionOutcomeLabel(status: ActionExecutionStatus): string {
    if (status === ActionExecutionStatus.SUCCESS) {
        return 'Successfully Executed Action: ';
    }
    if (status === ActionExecutionStatus.ERROR) {
        return 'Failed To Execute Action: ';
    }
    return 'Executed Action: ';
}

export function getActionExecutionDetailPath(actionSpecId: string): string {
    return `${DEFAULT_WORKFLOW_ROUTE}/actions/${actionSpecId}`;
}
